/**
 * DTemplate Product Slide
 * The product as one mom meets it: a coach for any wish, and for a wish
 * seen before, the app's own answer — the live products side by side, each
 * under its wish, with what its wishes added beneath it
 */

import React from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { ENGINE_KINDS, ENGINE_ORDER, EngineIcon } from '@components/wish-story/engines';
import { FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { KIND_NAMES } from '@components/press/pitch-deck/slides/deck-kinds';
import { DemoPhone } from '@components/press/pitch-deck/slides/demo-phone';
import { DECK_DEMOS } from '@components/press/pitch-deck/slides/dtemplate/demos';
import { SplitSlide, useArrive } from '@components/press/pitch-deck/slides/dtemplate/frame';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * For a product company, screenshots beat diagrams (the pitch notes in
 * docs/deck_inspiration), so the app itself is on the slide, labelled as
 * recordings of it.
 *
 * June, 2026-10-07: "What it looks like for a single mom, baseline is a
 * Claude coach, but if a wish could be helped by something that has been
 * seen before, then she gets a immersive experience into a personalized
 * overview and specific things that can help." And: "Include the datafact
 * below each product of what the prefetch data, expert models, and
 * enhanced capabilities are that came out of each wish." Four products
 * leave too little width for the kinds' names on every phone, so each
 * line under a phone wears its kind's icon and colour, and one key below
 * names the three in full (How we build's names: Enhanced Assistant
 * Capabilities, as June asked on 2026-09-23). Next are Push Button
 * Childcare and School Calendar. And (2026-10-09) the baseline includes
 * "a private calendar and project tracker for drafting - so she can get
 * value without providing any private information".
 *
 * Each recording starts once its phone has arrived; still (reduced
 * motion, or `?capture=1`, how decks are exported) each phone shows its
 * still, a frame with the answer on screen (`DECK_DEMOS`).
 */

export const DProductSlide: React.FC = () => (
  <SplitSlide
    section="Product"
    question="What does it look like for a real mom?"
    headline="Every wish gets a coach. A wish we’ve seen before gets the full experience."
    points={[
      <>
        <strong>The baseline:</strong> a Claude coach for any wish, and a private calendar and project tracker for
        drafting, so she gets value without sharing anything private.
      </>,
      <>
        <strong>A wish seen before:</strong> an immersive, personalized overview, and the specific things that help.
      </>,
      <>
        <strong>Live:</strong> Adventures, Growth Curves, Commute Optimizer and Kids Clothes.
      </>,
      <>
        <strong>Next:</strong> Push Button Childcare, with sitters she already knows and trusts, and School Calendar, so
        no day off school takes her by surprise.
      </>,
    ]}
    picture={<DemoRow />}
  />
);

/** Each product: its name, its wish, its phone, and what its wishes added;
 *  then the key to the three kinds. */
const DemoRow: React.FC = () => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const arrive = useArrive();
  return (
    <Stage>
      <Row style={{ gridTemplateColumns: `repeat(${DECK_DEMOS.length}, 226px)` }}>
        {DECK_DEMOS.map((demo, i) => (
          <Column key={demo.key} {...arrive(i + 1)}>
            <Name>{demo.title}</Name>
            <WishSpot>
              <Wish>“{demo.wish}”</Wish>
            </WishSpot>
            <DemoPhone screen={demo.screen} startAfter={still ? null : 1.2 + i * 0.12} width={196} still={demo.still} />
            <Built>
              {ENGINE_ORDER.map((kind) => (
                <BuiltRow key={kind}>
                  <KindDisc style={{ background: ENGINE_KINDS[kind].fill }}>
                    <EngineIcon engine={kind} size={13} color={ENGINE_KINDS[kind].iconInk} />
                  </KindDisc>
                  <span>
                    <Hidden>{KIND_NAMES[kind]}: </Hidden>
                    {demo.built[kind]}
                  </span>
                </BuiltRow>
              ))}
            </Built>
          </Column>
        ))}
      </Row>
      <Key {...arrive(DECK_DEMOS.length + 1)} aria-hidden="true">
        {ENGINE_ORDER.map((kind) => (
          <KeyItem key={kind}>
            <KindDisc style={{ background: ENGINE_KINDS[kind].fill }}>
              <EngineIcon engine={kind} size={13} color={ENGINE_KINDS[kind].iconInk} />
            </KindDisc>
            {KIND_NAMES[kind]}
          </KeyItem>
        ))}
      </Key>
      <Caption>Recorded in the app</Caption>
    </Stage>
  );
};

const Stage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Row = styled.div`
  display: grid;
  column-gap: 18px;
`;

const Column = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Name = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${colors.brand.exxxtraDark};
`;

/* Room for a two-line wish, the bubble at its foot, so a one-line wish
   leaves every phone level with the others. */
const WishSpot = styled.div`
  height: 84px;
  margin-bottom: 14px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

/* The wish as a mom writes it, plainly: no glass on a slide. */
const Wish = styled.p`
  margin: 0;
  padding: 9px 13px;
  border-radius: 18px 18px 6px 18px;
  background: ${colors.white};
  box-shadow: 0 8px 18px ${colors.shadowSoft};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 16px;
  line-height: 1.3;
  color: ${colors.neutral.extraDark};
  text-wrap: balance;
`;

/* What went into the answer, over the phone's faded foot: a line for each
   kind, in How we build's order and colours. */
const Built = styled.div`
  margin-top: -40px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
`;

const BuiltRow = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: start;
  column-gap: 8px;
  text-align: left;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 15px;
  line-height: 1.3;
  color: ${colors.neutral.darkMedium};
  text-wrap: pretty;
`;

const KindDisc = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/* Read aloud, not shown: the kind each line is, which the disc shows. */
const Hidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

const Key = styled(motion.div)`
  margin-top: 30px;
  display: flex;
  gap: 26px;
`;

const KeyItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  color: ${colors.neutral.extraDark};
`;

const Caption = styled.div`
  margin-top: 16px;
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.highlight.light};
`;
